import { Link } from '@tanstack/react-router'
import { useMemo } from 'react'

import { AssessmentsFilterRow } from './AssessmentsFilterRow'
import { useSurveyStore } from '../stores/surveyStore'
import type { ColumnDef } from '@tanstack/react-table'
import type { ConvexAssessmentListItem } from '../services/assessments.convex'
import { DataTable } from '@/components/common/data-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

// Re-exported so AssessmentsFilterRow can import it from here
export interface AssessmentFilters {
  employeeName?: string
  employeeUserIds?: string
  status?: string
  dateFrom?: string
  dateTo?: string
}

function ActionCell({
  assessment,
  currentUserId,
  isDirectReportsView,
}: {
  assessment: ConvexAssessmentListItem
  currentUserId: string
  isDirectReportsView: boolean
}) {
  const getSurveyProgress = useSurveyStore((s) => s.getSurveyProgress)
  const responses = assessment.responses
  const currentUserResponse = responses.find((r) => r.userId === currentUserId)
  const allCompleted =
    responses.length > 0 && responses.every((r) => r.completedAt)

  if (currentUserResponse && !currentUserResponse.completedAt) {
    const progress = getSurveyProgress(currentUserResponse._id)

    // Server draftPage is the source of truth for cross-device resume.
    // Local Zustand page is used as fallback for the current session.
    const serverPage = currentUserResponse.draftPage ?? null
    const localPage = progress?.currentPage ?? null
    // Prefer whichever is further along (server may lag if Zustand hasn't
    // navigated yet, but both fire on every page change so they stay in sync).
    const resumePage = Math.max(serverPage ?? 0, localPage ?? 0)

    const hasProgress =
      resumePage > 0 || (!!progress && Object.keys(progress.answers).length > 0)

    return (
      <Button asChild size="sm">
        <Link
          to="/assessments/$assessmentId/responses/$id"
          params={{
            assessmentId: assessment._id,
            id: currentUserResponse._id,
          }}
          search={{ page: resumePage }}
          state={
            isDirectReportsView
              ? ({ from: 'Direct Reports' } as never)
              : undefined
          }
        >
          {hasProgress ? 'Continue' : 'Take Survey'}
        </Link>
      </Button>
    )
  }

  if (allCompleted) {
    return (
      <Button asChild variant="outline" size="sm">
        <Link
          to="/assessments/$assessmentId"
          params={{ assessmentId: assessment._id }}
          state={
            isDirectReportsView
              ? ({ from: 'Direct Reports' } as never)
              : undefined
          }
        >
          View Results
        </Link>
      </Button>
    )
  }

  return (
    <Button asChild variant="outline" size="sm">
      <Link
        to="/assessments/$assessmentId"
        params={{ assessmentId: assessment._id }}
        state={
          isDirectReportsView
            ? ({ from: 'Direct Reports' } as never)
            : undefined
        }
      >
        View Assessment
      </Link>
    </Button>
  )
}

export interface AssessmentsTableProps {
  assessments: ConvexAssessmentListItem[]
  currentUserId: string
  isLoading?: boolean
  /** Whether there are more pages to load */
  hasMore?: boolean
  /** Whether the next page is currently loading */
  isLoadingMore?: boolean
  /** Callback to load the next page */
  onLoadMore?: () => void
  onFilterChange?: (filters: AssessmentFilters) => void
  isDirectReportsView?: boolean
}

function getAssessmentStatus(
  assessment: ConvexAssessmentListItem,
  currentUserId: string,
  isDirectReportsView: boolean,
) {
  const { responses } = assessment
  const completedCount = responses.filter((r) => r.completedAt).length
  const totalCount = responses.length
  const currentUserResponse = responses.find((r) => r.userId === currentUserId)

  if (completedCount === totalCount && totalCount > 0) {
    return { label: 'Completed', variant: 'default' as const }
  }
  if (completedCount === 0) {
    return { label: 'Pending', variant: 'secondary' as const }
  }
  if (currentUserResponse && !currentUserResponse.completedAt) {
    return { label: 'Waiting for you', variant: 'destructive' as const }
  }
  return {
    label: isDirectReportsView
      ? 'Waiting for Direct Report'
      : 'Waiting for manager',
    variant: 'outline' as const,
  }
}

function formatDate(ts: number | null): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function AssessmentsTable({
  assessments,
  currentUserId,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onFilterChange,
  isDirectReportsView = false,
}: AssessmentsTableProps) {
  const columns = useMemo<ColumnDef<ConvexAssessmentListItem>[]>(
    () => [
      {
        accessorKey: 'employee.name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="font-medium">
            {row.original.employee.name ?? row.original.employee.email ?? '—'}
          </div>
        ),
      },
      {
        accessorKey: 'assessmentType.name',
        header: 'Title',
        cell: ({ row }) => (
          <div>{row.original.assessmentType?.name ?? '—'}</div>
        ),
      },
      {
        id: 'position',
        header: 'Position',
        cell: ({ row }) => (
          <div>{row.original.assessmentType?.position?.level ?? '—'}</div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: ({ row }) => <div>{formatDate(row.original.createdAt)}</div>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = getAssessmentStatus(
            row.original,
            currentUserId,
            isDirectReportsView,
          )
          return <Badge variant={status.variant}>{status.label}</Badge>
        },
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => (
          <ActionCell
            assessment={row.original}
            currentUserId={currentUserId}
            isDirectReportsView={isDirectReportsView}
          />
        ),
      },
    ],
    [currentUserId, isDirectReportsView],
  )

  if (isLoading) {
    return (
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="relative w-full overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50 border-b transition-colors">
                {['Name', 'Title', 'Position', 'Date', 'Status', 'Action'].map(
                  (h) => (
                    <TableHead
                      key={h}
                      className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"
                    >
                      {h}
                    </TableHead>
                  ),
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow
                  key={i}
                  className="hover:bg-muted/50 border-b transition-colors"
                >
                  {Array.from({ length: 5 }).map((__, j) => (
                    <TableCell
                      key={j}
                      className="p-2 align-middle whitespace-nowrap"
                    >
                      <div className="h-4 bg-muted animate-pulse rounded" />
                    </TableCell>
                  ))}
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <div className="h-8 bg-muted animate-pulse rounded w-20" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  const filterRow = onFilterChange ? (
    <AssessmentsFilterRow
      isDirectReportsView={isDirectReportsView}
      onFilterChange={onFilterChange}
    />
  ) : undefined

  return (
    <div className="space-y-4">
      <DataTable
        columns={columns}
        data={assessments}
        emptyMessage="No assessments found."
        filterRow={filterRow}
      />
      {onLoadMore && hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}
